import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import type { SuggestedFix, SuggestedFixKind } from '../../suggestedFixes';

/**
 * Each line of "What we changed" (`dialog.fixes.<kind>`, plural, and its
 * `<kind>How`), as literal keys so the locale guards see every one. The
 * barcode cases count the products that get a generated barcode
 * (bos-app#232).
 */
const FIX_COPY = {
    imei: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeImei',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeImeiHow',
    },
    scientificNotation: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeScientific',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeScientificHow',
    },
    notDigits: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeNotDigits',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.barcodeNotDigitsHow',
    },
    variantFields: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.variantFields',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.variantFieldsHow',
    },
    groupFields: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.groupFields',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.groupFieldsHow',
    },
    sharedBarcode: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.sharedBarcode',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.sharedBarcodeHow',
    },
    alternates: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.alternates',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.alternatesHow',
    },
} as const satisfies Record<SuggestedFixKind, { title: string; how: string }>;

/** One fix of the summary, as `suggestedFixesSummaryOf` lists it. */
type SuggestedFixLineProps = SuggestedFix;

/** A barcode case's restocked products: they keep their barcode (#232 rule 2). */
const RESTOCKS_KEPT = 'catalogImport.products.bulk.restocksKeep';

/**
 * One line of "What we changed": the kind's count, a barcode case's
 * restocked products apart ("1 restocked product keeps its barcode", the
 * title when no product of it gets a generated barcode), the rows its
 * default changed ("112 rows changed", none for the extra barcodes) and how.
 */
export const SuggestedFixLine: React.FC<SuggestedFixLineProps> = ({
    kind,
    count,
    restocks = 0,
    changedRows,
}) => {
    const { t } = useTranslation('pages');
    const kept =
        restocks > 0
            ? t(RESTOCKS_KEPT, { count: restocks, rows: countParam(restocks) })
            : null;
    return (
        <div className="flex flex-col gap-0.5">
            <Typography type="body-sm" weight="medium">
                {count > 0
                    ? t(FIX_COPY[kind].title, {
                          count,
                          rows: countParam(count),
                      })
                    : kept}
            </Typography>
            {count > 0 && kept !== null ? (
                <Typography type="body-xs">{kept}</Typography>
            ) : null}
            {changedRows === null ? null : (
                <Typography type="body-xs">
                    {t(
                        'catalogImport.products.suggestedFixes.dialog.rowsChanged',
                        { count: changedRows, rows: countParam(changedRows) },
                    )}
                </Typography>
            )}
            <Typography color="muted" type="body-xs">
                {t(FIX_COPY[kind].how)}
            </Typography>
        </div>
    );
};
